import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { GitBranch, HelpCircle } from 'lucide-react'

export interface ConditionShapeNodeData {
  label: string
  condition?: string
  status?: 'idle' | 'evaluating' | 'true' | 'false'
  onDelete?: () => void
  [key: string]: unknown
}

const statusConfig = {
  idle: { color: '#f59e0b', glow: false },
  evaluating: { color: '#f59e0b', glow: true },
  true: { color: '#10b981', glow: false },
  false: { color: '#ef4444', glow: false },
}

interface ConditionShapeNodeProps {
  data: ConditionShapeNodeData
  selected?: boolean
}

function ConditionShapeNodeComponent({ data, selected }: ConditionShapeNodeProps) {
  const { label, condition, status = 'idle' } = data
  const config = statusConfig[status]

  return (
    <div
      className="relative group"
      style={{ width: 160, height: 100 }}
    >
      {/* Diamond shape - classic for decision nodes */}
      <svg
        viewBox="0 0 160 100"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: selected || config.glow
            ? `drop-shadow(0 0 12px ${config.color}50)`
            : 'drop-shadow(0 4px 6px rgba(0,0,0,0.07))',
        }}
      >
        <defs>
          <linearGradient id={`condition-grad-${status}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={config.color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        {/* Diamond path */}
        <path
          d="M 80 6 L 154 50 L 80 94 L 6 50 Z"
          fill={`url(#condition-grad-${status})`}
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        {/* Inner diamond accent */}
        <path
          d="M 80 16 L 140 50 L 80 84 L 20 50 Z"
          fill="none"
          stroke={config.color}
          strokeWidth="1"
          strokeOpacity="0.2"
        />
      </svg>

      {/* Content - centered in diamond */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        <div
          className="p-1.5 rounded-lg mb-1"
          style={{ backgroundColor: `${config.color}20` }}
        >
          {condition ? (
            <GitBranch className="w-4 h-4" style={{ color: config.color }} />
          ) : (
            <HelpCircle className="w-4 h-4" style={{ color: config.color }} />
          )}
        </div>
        <p className="text-xs font-semibold text-gray-900 dark:text-white text-center leading-tight">
          {label}
        </p>
      </div>

      {/* Evaluating animation */}
      {status === 'evaluating' && (
        <div className="absolute inset-0 pointer-events-none">
          <svg viewBox="0 0 160 100" className="w-full h-full">
            <path
              d="M 80 6 L 154 50 L 80 94 L 6 50 Z"
              fill="none"
              stroke={config.color}
              strokeWidth="2"
              strokeDasharray="8 4"
              className="animate-[dash_1s_linear_infinite]"
            />
          </svg>
        </div>
      )}

      {/* Target Handle - top */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
        style={{ backgroundColor: config.color }}
      />

      {/* Source Handles - left (false) and right (true) */}
      <Handle
        type="source"
        position={Position.Left}
        id="false"
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-left-1.5"
        style={{ backgroundColor: '#ef4444', top: '50%', transform: 'translateY(-50%)' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-right-1.5"
        style={{ backgroundColor: '#10b981', top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Labels for branches */}
      <span className="absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full text-[10px] font-medium text-red-500 pr-1">
        No
      </span>
      <span className="absolute -right-1 top-1/2 -translate-y-1/2 translate-x-full text-[10px] font-medium text-emerald-500 pl-1">
        Yes
      </span>
    </div>
  )
}

export const ConditionShapeNode = memo(ConditionShapeNodeComponent)
export default ConditionShapeNode
